import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormatDate } from '../../hooks/useFormatDate';
import { fireAwardEvent, type ShareNetwork } from '../../lib/awardEvents';
import { downloadFile, safeFilename } from '../../lib/downloadFile';

interface Award {
  id: string;
  image: string;
  issuedOn: string;
  expires?: string;
  recipient: { name: string };
  badge: {
    name: string;
    description: string;
    image: string;
    criteria: { narrative: string | string[] };
    issuer: { name: string; url: string; email: string; linkedinOrganizationId?: string };
  };
  revoked?: boolean;
  revocationReason?: string;
}

function linkedinAddToProfileUrl(award: Award): string | null {
  const orgId = award.badge.issuer.linkedinOrganizationId;
  const orgName = award.badge.issuer.name;
  // Show the button whenever we have at least an issuer name. organizationId
  // is preferred — when present and valid, LinkedIn pre-fills the cert with
  // the verified Company Page. organizationName is a required fallback when
  // the issuer isn't on LinkedIn AND a sanity-check display value when an ID
  // is set but doesn't resolve to the expected page.
  if (!orgId && !orgName) return null;
  const issued = new Date(award.issuedOn);
  const verifyUrl = `${window.location.origin}/verify/${award.id.split('/').pop() || award.id}`;
  const params = new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: award.badge.name,
    issueYear: String(issued.getFullYear()),
    issueMonth: String(issued.getMonth() + 1),
    certUrl: verifyUrl,
    certId: award.id.split('/').pop() || award.id,
  });
  if (orgId)   params.set('organizationId', orgId);
  if (orgName) params.set('organizationName', orgName);
  if (award.expires) {
    const exp = new Date(award.expires);
    params.set('expirationYear', String(exp.getFullYear()));
    params.set('expirationMonth', String(exp.getMonth() + 1));
  }
  return `https://www.linkedin.com/profile/add?${params.toString()}`;
}

const API_URL = import.meta.env.VITE_CERTIFY_BASE_URL || import.meta.env.VITE_API_URL;

export function AwardView() {
  const { guid } = useParams<{ guid: string }>();
  const { t } = useTranslation('common');
  const formatDate = useFormatDate();
  const [award, setAward] = useState<Award | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [dlOpen, setDlOpen] = useState(false);
  const dlRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!guid) return;
    let cancelled = false;
    const MAX_ATTEMPTS = 3;

    const load = async (attempt: number): Promise<void> => {
      try {
        const r = await fetch(`${API_URL}/certify-badge/award/${guid}`);
        // A genuine 404 is terminal — the award really doesn't exist.
        if (r.status === 404) {
          if (!cancelled) { setError(t('award.notFound')); setLoading(false); }
          return;
        }
        // Any other non-OK (5xx/transient) is retryable — do NOT show "not found".
        if (!r.ok) throw new Error(`status ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setAward(data);
        fireAwardEvent(guid, 'view');
        setLoading(false);
      } catch {
        if (cancelled) return;
        if (attempt < MAX_ATTEMPTS) {
          setTimeout(() => { if (!cancelled) void load(attempt + 1); }, 600 * attempt);
          return;
        }
        // Retries exhausted on a transient error — tell the user it's temporary,
        // not that the badge is gone (which would be wrong and alarming).
        setError(t('award.unavailable', 'This badge is temporarily unavailable. Please refresh in a moment.'));
        setLoading(false);
      }
    };

    void load(1);
    return () => { cancelled = true; };
  }, [guid, t]);

  // Close the download menu on outside click / Escape.
  useEffect(() => {
    if (!dlOpen) return;
    const onClick = (e: MouseEvent) => { if (!dlRef.current?.contains(e.target as Node)) setDlOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDlOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [dlOpen]);

  if (loading) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="h-8 w-8 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
    </div>
  );

  if (error || !award) return (
    <div className="flex h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">{error || t('award.notFound')}</h1>
        <a href="/" className="text-primary-600 hover:underline">{t('award.verify.goHome', 'Go home')}</a>
      </div>
    </div>
  );

  const isExpired = award.expires && new Date(award.expires) < new Date();
  const criteria = Array.isArray(award.badge.criteria.narrative)
    ? award.badge.criteria.narrative
    : typeof award.badge.criteria.narrative === 'string'
      ? award.badge.criteria.narrative.split('.').map(s => s.trim()).filter(Boolean)
      : [t('award.view.defaultCriteria')];

  // ---- sharing (tier 1) — activates the per-network share tracking + the
  // social share-card images generated at award time.
  function share(network: ShareNetwork) {
    if (!award) return;
    fireAwardEvent(guid!, 'share', network);
    const url = window.location.href;
    const text = t('award.view.shareMessage', {
      badge: award.badge.name,
      issuer: award.badge.issuer.name,
      defaultValue: `I earned the "${award.badge.name}" credential from ${award.badge.issuer.name}.`,
    });
    const e = encodeURIComponent;
    let target = '';
    switch (network) {
      case 'linkedin': target = `https://www.linkedin.com/sharing/share-offsite/?url=${e(url)}`; break;
      case 'twitter':  target = `https://twitter.com/intent/tweet?text=${e(text)}&url=${e(url)}`; break;
      case 'facebook': target = `https://www.facebook.com/sharer/sharer.php?u=${e(url)}`; break;
      case 'whatsapp': target = `https://api.whatsapp.com/send?text=${e(`${text} ${url}`)}`; break;
      case 'email':    target = `mailto:?subject=${e(text)}&body=${e(`${text}\n\n${url}`)}`; break;
      case 'copy':
        navigator.clipboard?.writeText(url).catch(() => {});
        setCopied(true); setTimeout(() => setCopied(false), 1600);
        return;
      default: return;
    }
    if (network === 'email') window.location.href = target;
    else window.open(target, '_blank', 'noopener,noreferrer,width=640,height=680');
  }

  const shareButtons: { key: ShareNetwork; label: string; brand?: string; icon: JSX.Element }[] = [
    { key: 'linkedin', label: 'LinkedIn', icon: <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.44-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46zM5.34 7.43a2.06 2.06 0 110-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.55V9h3.57zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" /> },
    { key: 'twitter', label: 'X', icon: <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.66l-5.21-6.82-5.97 6.82H1.87l7.73-8.83L1.45 2.25h6.83l4.71 6.23zm-1.16 17.52h1.83L7.01 4.13H5.04z" /> },
    { key: 'facebook', label: 'Facebook', icon: <path d="M24 12a12 12 0 10-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.92-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0024 12z" /> },
    { key: 'whatsapp', label: 'WhatsApp', icon: <path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.53.15-.18.2-.3.3-.5.1-.2.05-.38-.02-.53-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.53.07-.8.38-.28.3-1.05 1.02-1.05 2.49s1.07 2.89 1.22 3.09c.15.2 2.11 3.22 5.11 4.51.71.31 1.27.49 1.71.63.72.23 1.37.2 1.89.12.58-.09 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35zM12.05 21.5a9.44 9.44 0 01-4.81-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.46 9.46 0 1117.36-5.23 9.46 9.46 0 01-9.32 9.65z" /> },
    { key: 'email', label: t('award.view.shareEmail', 'Email'), icon: <><rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M3 7l9 6 9-6" fill="none" stroke="currentColor" strokeWidth="2" /></> },
    { key: 'copy', label: t('award.share.copyLink', 'Copy link'), icon: <><path d="M10 13a5 5 0 007.5.5l3-3A5 5 0 0013.5 3.5l-1.7 1.7" fill="none" stroke="currentColor" strokeWidth="2" /><path d="M14 11a5 5 0 00-7.5-.5l-3 3A5 5 0 0010.5 20.5l1.7-1.7" fill="none" stroke="currentColor" strokeWidth="2" /></> },
  ];

  const liUrl = linkedinAddToProfileUrl(award);
  const revoked = !!award.revoked;
  const issuerInitials = (award.badge.issuer.name || '?')
    .split(/\s+/).filter(w => /[a-zA-Z0-9]/.test(w))
    .slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '★';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* trust ribbon — full width */}
        {revoked ? (
          <div className="flex items-center justify-center gap-2 bg-red-50 border-b border-red-100 py-2.5 text-[12.5px] font-semibold text-red-700">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></svg>
            {t('award.view.revoked', 'This credential has been revoked')}
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 bg-emerald-50 border-b border-emerald-100 py-2.5 text-[12.5px] font-semibold text-emerald-700">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 2l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V5z" /><path d="M9 12l2 2 4-4" /></svg>
            {t('award.view.verifiedCredential', 'Verified credential · Open Badges 2.0 & 3.0')}
          </div>
        )}

        <div className="grid md:grid-cols-[minmax(0,380px)_1fr] lg:grid-cols-[minmax(0,460px)_1fr] xl:grid-cols-[minmax(0,540px)_1fr]">
          {/* ===== LEFT: credential hero — the badge is the subject of the page ===== */}
          <aside
            className="flex flex-col items-center justify-start gap-5 border-b md:border-b-0 md:border-r border-gray-100 p-8 lg:p-10"
            style={{ background: 'radial-gradient(120% 90% at 50% 10%, #efe9ff 0%, #f7f4ff 46%, #ffffff 100%)' }}
          >
            <img
              src={award.image || award.badge.image}
              alt={award.badge.name}
              className="w-full max-w-[450px] aspect-square object-contain mx-auto"
            />
            <div className="flex items-center gap-2.5 rounded-full border border-gray-200 bg-white pl-2 pr-4 py-1.5 shadow-sm">
              <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-gradient-to-br from-primary-600 to-purple-400 text-[11px] font-bold text-white">{issuerInitials}</span>
              <span className="text-left leading-tight">
                <small className="block text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400">{t('award.view.issuedBy')}</small>
                <a href={award.badge.issuer.url} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-gray-800 hover:text-primary-700">{award.badge.issuer.name}</a>
              </span>
            </div>
          </aside>

          {/* ===== RIGHT: details + actions ===== */}
          <div className="p-8 text-left">
          <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-gray-400">{t('award.view.awardedTo', 'Awarded to')}</p>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 mt-1 mb-3 text-balance">{award.recipient.name}</h1>
          <span className="inline-block text-[15px] font-semibold text-primary-700 bg-primary-50 border border-primary-200 rounded-full px-4 py-1.5">
            {award.badge.name}
          </span>

          {/* meta chips */}
          <div className="flex flex-wrap gap-2 mt-5">
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5">
              <svg className="h-3.5 w-3.5 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18M8 2v4M16 2v4" /></svg>
              {t('award.view.issuedOn')} {formatDate(award.issuedOn)}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-600 bg-gray-50 border border-gray-200 rounded-full px-3 py-1.5">
              <svg className="h-3.5 w-3.5 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 21V7a2 2 0 012-2h12a2 2 0 012 2v14" /><path d="M9 21v-6h6v6" /></svg>
              {t('award.view.issuedBy')}{' '}
              <a href={award.badge.issuer.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline font-semibold">{award.badge.issuer.name}</a>
            </span>
          </div>

          <p className="text-gray-600 mt-5 mb-6 text-[14.5px] max-w-lg">{award.badge.description}</p>

          <div className="text-left border-t border-gray-100 pt-6">
            <h3 className="text-[11px] font-bold tracking-[0.12em] uppercase text-gray-400 mb-3">{t('award.view.criteria')}</h3>
            <ul className="space-y-2">
              {criteria.map((c, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="flex-none mt-0.5 w-[18px] h-[18px] rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 6" /></svg>
                  </span>
                  <span className="text-gray-600 text-sm">{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {isExpired && (
            <div className="mt-4 px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm">
              {t('award.view.expired', { date: formatDate(award.expires!) })}
            </div>
          )}

          {/* ===== ACTION ZONE ===== */}
          {revoked ? (
            <div className="border-t border-gray-100 mt-7 pt-6">
              <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-sm text-left text-red-800">
                <p className="font-semibold">{t('award.view.revokedTitle', 'This credential is no longer valid.')}</p>
                <p className="mt-1 text-red-700/90">{t('award.view.revokedBody', 'The issuer has revoked this award, so it can no longer be shared, downloaded, or verified as active.')}</p>
                {award.revocationReason && (
                  <p className="mt-2 text-xs text-red-700/80"><span className="font-semibold">{t('award.view.revokedReason', 'Reason')}:</span> {award.revocationReason}</p>
                )}
              </div>
            </div>
          ) : (
          <div className="border-t border-gray-100 mt-7 pt-6">

            {/* tier 1 — share (primary) */}
            <p className="flex items-center gap-2.5 text-[10.5px] font-bold tracking-[0.13em] uppercase text-gray-400 mb-3">
              {t('award.view.shareTitle', 'Share this credential')}
              <span className="flex-1 h-px bg-gray-100" />
            </p>
            <div className="flex flex-wrap gap-2.5">
              {shareButtons.map(b => (
                <button key={b.key} type="button" onClick={() => share(b.key)} title={b.label} aria-label={b.label}
                  className="group relative w-[46px] h-[46px] rounded-xl border border-gray-200 bg-gray-50 text-gray-600 grid place-items-center transition hover:-translate-y-0.5 hover:border-primary-300 hover:text-primary-600 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill={b.key === 'linkedin' || b.key === 'twitter' || b.key === 'facebook' || b.key === 'whatsapp' ? 'currentColor' : 'none'}>{b.icon}</svg>
                  {b.key === 'copy' && (
                    <span className={`absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-bold text-emerald-600 transition-opacity ${copied ? 'opacity-100' : 'opacity-0'}`}>
                      {t('award.share.copied', 'Copied!')}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* tier 2 — add & download (secondary) */}
            <p className="flex items-center gap-2.5 text-[10.5px] font-bold tracking-[0.13em] uppercase text-gray-400 mt-8 mb-3">
              {t('award.view.addAndDownload', 'Add & download')}
              <span className="flex-1 h-px bg-gray-100" />
            </p>
            <div className="flex flex-wrap gap-3">
              {liUrl && (
                <a href={liUrl} target="_blank" rel="noopener noreferrer"
                  onClick={() => fireAwardEvent(guid!, 'click_linkedin_addtoprofile')}
                  className="inline-flex items-center gap-2 px-[18px] py-[11px] rounded-xl text-sm font-semibold text-white bg-[#0a66c2] hover:bg-[#004182] shadow-sm transition hover:-translate-y-0.5">
                  <svg className="h-[17px] w-[17px]" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                  {t('award.view.addToLinkedIn', 'Add to LinkedIn profile')}
                </a>
              )}
              <div className="relative" ref={dlRef}>
                <button type="button" onClick={() => setDlOpen(o => !o)} aria-haspopup="true" aria-expanded={dlOpen}
                  className="inline-flex items-center gap-2 px-[18px] py-[11px] rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition hover:-translate-y-0.5">
                  <svg className="h-[17px] w-[17px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12M7 10l5 5 5-5M5 21h14" /></svg>
                  {t('award.view.download', 'Download')}
                  <svg className="h-3 w-3 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M6 9l6 6 6-6" /></svg>
                </button>
                {dlOpen && (
                  <div role="menu" className="absolute bottom-[calc(100%+8px)] left-0 min-w-[224px] bg-white border border-gray-200 rounded-2xl shadow-xl p-1.5 z-10">
                    <button role="menuitem" type="button"
                      onClick={() => { setDlOpen(false); fireAwardEvent(guid!, 'download'); downloadFile(award.image || award.badge.image, safeFilename(award.badge.name)); }}
                      className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[13.5px] font-semibold text-gray-800 hover:bg-primary-50 hover:text-primary-700">
                      <span className="flex-none w-[30px] h-[30px] rounded-lg bg-gray-50 text-primary-600 grid place-items-center">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                      </span>
                      <span>{t('award.view.downloadImage', 'Badge image')}<small className="block font-medium text-gray-400 text-[11.5px]">{t('award.view.downloadImageHint', 'PNG · square')}</small></span>
                    </button>
                    <a role="menuitem" href={`${API_URL}/certify-badge/award/${guid}/pdf`} target="_blank" rel="noopener noreferrer"
                      onClick={() => { setDlOpen(false); fireAwardEvent(guid!, 'click_pdf'); }}
                      className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[13.5px] font-semibold text-gray-800 hover:bg-primary-50 hover:text-primary-700">
                      <span className="flex-none w-[30px] h-[30px] rounded-lg bg-gray-50 text-primary-600 grid place-items-center">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
                      </span>
                      <span>{t('award.view.certificatePdf', 'Certificate')}<small className="block font-medium text-gray-400 text-[11.5px]">{t('award.view.certificatePdfHint', 'PDF · print-ready')}</small></span>
                    </a>
                    <a role="menuitem" href={`${API_URL}/certify-badge/award/${guid}/vc`} target="_blank" rel="noopener noreferrer"
                      onClick={() => { setDlOpen(false); fireAwardEvent(guid!, 'click_vc'); }}
                      className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-left text-[13.5px] font-semibold text-gray-800 hover:bg-primary-50 hover:text-primary-700">
                      <span className="flex-none w-[30px] h-[30px] rounded-lg bg-gray-50 text-primary-600 grid place-items-center">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V5z" /><path d="M9 12l2 2 4-4" /></svg>
                      </span>
                      <span>{t('award.view.verifiableCredential', 'Verifiable Credential')}<small className="block font-medium text-gray-400 text-[11.5px]">{t('award.view.verifiableCredentialHint', 'Open Badges 3.0 · signed')}</small></span>
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* tier 3 — verify (ambient trust) */}
            <div className="flex items-center justify-center flex-wrap gap-2 mt-5 p-3 rounded-xl bg-gray-50 border border-gray-100 text-[12.5px] text-gray-600">
              <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V5z" /><path d="M9 12l2 2 4-4" /></svg>
              {t('award.view.independentlyVerifiable', 'Anyone can independently confirm this credential.')}
              <a href={`/verify/${guid}`} onClick={() => fireAwardEvent(guid!, 'click_verify')} className="text-primary-600 font-bold hover:underline">
                {t('award.view.viewVerification', 'View verification')} &rarr;
              </a>
            </div>
          </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
